import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Field, Modal, Alert, Label, Spinner } from "@/components/ui";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Plus, Heart, Droplet, Scale, Smartphone, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { k: "blood_pressure", label: "Blood pressure", icon: Heart, unit: "mmHg" },
  { k: "glucose", label: "Glucose", icon: Droplet, unit: "mg/dL" },
  { k: "weight", label: "Weight", icon: Scale, unit: "lbs" },
];

const CHART_MARGIN = { top: 8, right: 8, left: -18, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: "#5B6B84" };
const LINE_DOT = { r: 3 };

const synthReadings = () => {
  const out = [];
  const rand = (a, b) => Math.round((a + Math.random() * (b - a)) * 10) / 10;
  for (let d = 6; d >= 0; d--) {
    const t = new Date(); t.setDate(t.getDate() - d); t.setHours(8, 15, 0, 0);
    out.push({ kind: "blood_pressure", systolic: Math.round(rand(118, 138)), diastolic: Math.round(rand(72, 86)), unit: "mmHg", recorded_at: t.toISOString() });
    const g = new Date(t); g.setHours(20, 30, 0, 0);
    out.push({ kind: "glucose", value: Math.round(rand(95, 145)), unit: "mg/dL", recorded_at: g.toISOString() });
    if (d % 2 === 0) { const w = new Date(t); w.setHours(7); out.push({ kind: "weight", value: rand(158, 164), unit: "lbs", recorded_at: w.toISOString() }); }
  }
  return out;
};

export default function Vitals() {
  const [items, setItems] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [chartKind, setChartKind] = useState("blood_pressure");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => setItems(await api.listVitals()), []);
  useEffect(() => { load(); }, [load]);

  const runDemo = async () => {
    setSyncing(true);
    try {
      const r = await api.importVitals({ source: "demo_sync", readings: synthReadings() });
      await load();
      toast.success(`Synced ${r.imported} readings${r.skipped ? ` (${r.skipped} duplicates skipped)` : ""}`);
    } catch (e) { toast.error(e?.message || "Sync failed"); }
    finally { setSyncing(false); }
  };

  if (!items) return <Spinner />;

  const latest = (k) => items.find((v) => v.kind === k);
  const fmt = (v) => {
    if (!v) return "—";
    if (v.kind === "blood_pressure") return `${Math.round(v.systolic)}/${Math.round(v.diastolic)} mmHg`;
    if (v.kind === "glucose") return `${v.value} ${v.unit || "mg/dL"}`;
    if (v.kind === "weight") return `${v.value} ${v.unit || "lbs"}`;
    return "—";
  };

  // build 14-day chart series for selected kind
  const within = (iso, days) => { const t = Date.parse(iso); return t && t >= Date.now() - days * 864e5; };
  const chartData = (() => {
    const buckets = {};
    for (const v of items) {
      if (v.kind !== chartKind) continue;
      const iso = String(v.recorded_at || v.created_at || "").slice(0, 10);
      if (!iso || !within(v.recorded_at || v.created_at, 14)) continue;
      (buckets[iso] ||= []).push(v);
    }
    const out = [];
    for (let d = 13; d >= 0; d--) {
      const day = new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);
      const vals = buckets[day] || [];
      const label = new Date(day).toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
      if (chartKind === "blood_pressure") {
        const sys = vals.map((v) => v.systolic).filter(Boolean);
        const dia = vals.map((v) => v.diastolic).filter(Boolean);
        out.push({ day: label, systolic: sys.length ? Math.round(sys.reduce((a, b) => a + b, 0) / sys.length) : null, diastolic: dia.length ? Math.round(dia.reduce((a, b) => a + b, 0) / dia.length) : null });
      } else {
        const nums = vals.map((v) => Number(v.value)).filter((n) => !isNaN(n));
        out.push({ day: label, value: nums.length ? Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10 : null });
      }
    }
    return out;
  })();
  const hasChart = chartData.some((d) => (chartKind === "blood_pressure" ? d.systolic != null : d.value != null));

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="Vitals" title="Health readings"
        action={<Button variant="accent" data-testid="add-vital-button" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Log</Button>} />

      <Card data-testid="wearables-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-mint-tint flex items-center justify-center shrink-0"><Smartphone className="w-5 h-5 text-mint-dark" /></div>
          <div className="flex-1">
            <Label className="text-ink">Connect a wearable</Label>
            <p className="text-sm text-subtle mt-1">Apple Health, Health Connect & Fitbit sync from the KinBridge mobile app. Try a demo sync here to preview trends.</p>
            <Button variant="secondary" data-testid="sync-demo" onClick={runDemo} loading={syncing} className="mt-3">
              <RefreshCw className="w-4 h-4" /> Demo sync (7 days)
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {KINDS.map(({ k, label, icon: Icon }) => {
          const last = latest(k);
          return (
            <button key={k} data-testid={`vital-card-${k}`} onClick={() => setChartKind(k)}
              className={`text-left bg-white border rounded-xl p-4 transition-all ${chartKind === k ? "border-brand ring-2 ring-brand/15" : "border-line hover:border-brand-tint"}`}>
              <div className="flex items-center gap-2 text-subtle"><Icon className="w-4 h-4" /><span className="text-xs font-semibold uppercase">{label}</span></div>
              <div className="font-display text-2xl text-brand-dark mt-2">{fmt(last)}</div>
              <div className="text-xs text-subtle mt-1">{last ? new Date(last.recorded_at || last.created_at).toLocaleDateString() : "No readings yet"}</div>
            </button>
          );
        })}
      </div>

      <Card className="mt-4">
        <Label className="text-ink capitalize">{chartKind.replace(/_/g, " ")} · 14-day trend</Label>
        {hasChart ? (
          <div className="mt-3 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E1E8F2" />
                <XAxis dataKey="day" tick={AXIS_TICK} interval="preserveStartEnd" />
                <YAxis tick={AXIS_TICK} domain={["auto", "auto"]} />
                <Tooltip />
                {chartKind === "blood_pressure" ? (
                  <>
                    <Line type="monotone" dataKey="systolic" stroke="#22C99A" strokeWidth={2} connectNulls dot={false} />
                    <Line type="monotone" dataKey="diastolic" stroke="#0A2F5F" strokeWidth={2} connectNulls dot={false} />
                  </>
                ) : (
                  <Line type="monotone" dataKey="value" stroke="#0A2F5F" strokeWidth={2} connectNulls dot={LINE_DOT} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-subtle mt-3 py-8 text-center">Not enough data yet — log at least two days of readings.</p>
        )}
      </Card>

      <AddVitalModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await load(); toast.success("Vital logged"); }} />
    </div>
  );
}

function AddVitalModal({ open, onClose, onSaved }) {
  const ALL = [
    { k: "blood_pressure", label: "Blood pressure" }, { k: "glucose", label: "Glucose" },
    { k: "weight", label: "Weight" }, { k: "heart_rate", label: "Resting HR" },
    { k: "steps", label: "Steps" }, { k: "sleep_minutes", label: "Sleep" },
  ];
  const units = { glucose: "mg/dL", weight: "lbs", heart_rate: "bpm", steps: "steps" };
  const [kind, setKind] = useState("blood_pressure");
  const [sys, setSys] = useState(""); const [dia, setDia] = useState("");
  const [val, setVal] = useState(""); const [sh, setSh] = useState(""); const [sm, setSm] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null); setBusy(true);
    try {
      const body = { kind };
      if (kind === "blood_pressure") {
        body.systolic = parseFloat(sys); body.diastolic = parseFloat(dia); body.unit = "mmHg";
        if (isNaN(body.systolic) || isNaN(body.diastolic)) throw new Error("Enter valid systolic/diastolic");
      } else if (kind === "sleep_minutes") {
        const h = parseInt(sh || "0", 10), m = parseInt(sm || "0", 10);
        if ((h === 0 && m === 0) || isNaN(h) || isNaN(m)) throw new Error("Enter hours and/or minutes");
        body.value = h * 60 + m; body.unit = "min";
      } else {
        body.value = parseFloat(val); body.unit = units[kind];
        if (isNaN(body.value)) throw new Error("Enter a valid number");
      }
      await api.createVital(body);
      setSys(""); setDia(""); setVal(""); setSh(""); setSm("");
      onSaved();
    } catch (ex) { setErr(ex?.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Log vital" testid="add-vital-modal">
      <div className="flex flex-wrap gap-2 mb-4">
        {ALL.map(({ k, label }) => (
          <button key={k} data-testid={`vital-kind-${k}`} onClick={() => setKind(k)}
            className={`px-3.5 py-2 rounded-full text-xs font-semibold border ${kind === k ? "bg-brand text-white border-brand" : "bg-brand-tint text-brand-dark border-line"}`}>{label}</button>
        ))}
      </div>
      {kind === "blood_pressure" ? (
        <div className="flex gap-3">
          <Field label="Systolic" data-testid="vital-systolic-input" value={sys} onChange={(e) => setSys(e.target.value)} placeholder="120" className="w-full" inputMode="numeric" />
          <Field label="Diastolic" data-testid="vital-diastolic-input" value={dia} onChange={(e) => setDia(e.target.value)} placeholder="80" className="w-full" inputMode="numeric" />
        </div>
      ) : kind === "sleep_minutes" ? (
        <div className="flex gap-3">
          <Field label="Hours" data-testid="vital-sleep-hours-input" value={sh} onChange={(e) => setSh(e.target.value)} placeholder="7" className="w-full" inputMode="numeric" />
          <Field label="Minutes" data-testid="vital-sleep-minutes-input" value={sm} onChange={(e) => setSm(e.target.value)} placeholder="30" className="w-full" inputMode="numeric" />
        </div>
      ) : (
        <Field label={`Value (${units[kind]})`} data-testid="vital-value-input" value={val} onChange={(e) => setVal(e.target.value)} placeholder="110" inputMode="numeric" />
      )}
      <Alert testid="add-vital-error">{err}</Alert>
      <Button data-testid="save-vital-button" onClick={save} loading={busy} className="w-full mt-1">Log vital</Button>
    </Modal>
  );
}
